import { SelectAllDto } from '@osos/shared/api';
import {
  AnyObject,
  BulkUpdateEntriesPresenterModel,
  BulkUpgradeEntriesPresenterModel,
  ChangeStepOwnerModel,
  DataEntryPresenterModel,
  DataEntryStateEnum,
  DataEntryUpdatePositionPresenterModel,
  DataQueryPresenterModel,
  DataTypeEnum,
  FieldPresenterModel,
  KanbanCategoryFilter,
  ReferenceValueUpdateModel,
  RefProperty,
  Sort,
  TemplateExportTypeEnum,
  TemplateLevelEnum,
  TemplateTypeEnum,
} from '@osos/shared/types';

export namespace EntryActions {
  export class TransitoryFetchEntries {
    static readonly type = '[Entry] TransitoryFetch Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        skip?: number;
        limit?: number;
        searchTerm?: string;
        filterBy?: string[];
        filterCriteria?: any;
        entryIds?: string[];
        viewId?: string;
        reportId?: string;
        /**
         * It is used to fetch the related entity and normalize the entry after parsing it
         */
        normalizeEntry?: boolean;
        excludedIds?: string[];
        withCount?: boolean;
        selectable?: boolean;
        sort?: Sort;
        appliedQuickFilters?: string[];
        skipTextSearch?: boolean;
      },
    ) {}
  }

  export class FetchUserCollectionEntries {
    static readonly type = '[Entry] Fetch User Collection Entries';
    constructor(
      public payload: {
        collectionId: string;
        entityId: string;
        skip?: number;
        limit?: number;
        searchTerm?: string;
        dataQuery?: DataQueryPresenterModel;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterBy?: string[];
        state?: DataEntryStateEnum;
        sort?: Sort[];
        withCount?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }

  export class FetchUserCollectionEntriesTags {
    static readonly type = '[Entry Tags] Fetch User Collection Entries Tags';
    constructor(
      public payload: {
        collectionId: string;
        entityId: string;
        skip?: number;
        limit?: number;
        searchTerm?: string;
        dataQuery?: DataQueryPresenterModel;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterBy?: string[];
        state?: DataEntryStateEnum;
        sort?: Sort[];
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }
  export class TransitoryFetchReferenceFieldData {
    static readonly type = '[Entry] TransitoryFetch Reference Field Data';
    constructor(
      public payload: {
        entityId: string;
        fieldId: string;
        relatedEntryId?: string;
        cascadedParents?: { cascadedParent: RefProperty }[];
        skip?: number;
        limit?: number;
        searchTerm?: string;
        filterBy?: string[];
        entryIds?: string[];
        sort?: Sort;
        includeId?: Array<string>;
        field?: FieldPresenterModel;
        mainTitleType?: DataTypeEnum;
        mappedFieldsFilter?: DataQueryPresenterModel;
        parameters?: AnyObject;
      },
    ) {}
  }

  export class FetchRelationEntries {
    static readonly type = '[Entry] Fetch Entries For Relation';
    constructor(
      public payload: {
        entityId: string;
        viewId?: string;
        relationId: string;
        entryId: string;
        dataQuery?: DataQueryPresenterModel;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        search?: string;
        state?: DataEntryStateEnum;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        appliedTagsFilters?: string[];
        relationEntityId?: string;
      },
    ) {}
  }

  export class FetchRelationTreeEntries {
    static readonly type = '[Entry] Fetch Relation Tree Entries';
    constructor(
      public payload: {
        entityId: string;
        fieldNormalizedName: string;
        fieldSource?: string;
        startingItem?: string;
        viewId?: string;
        relationId: string;
        entryId: string;
        appliedTagsFilters?: string[];
        search?: string;
        appliedQuickFilters?: string[];
        dataQuery?: DataQueryPresenterModel;
      },
    ) {}
  }

  export class FetchRelationKanbanEntries {
    static readonly type = '[Entry] Fetch Kanban Entries For Relation';
    constructor(
      public payload: {
        entityId: string;
        viewId?: string;
        relationId: string;
        entryId: string;
        dataQuery?: DataQueryPresenterModel;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        search?: string;
        state?: DataEntryStateEnum;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        appliedTagsFilters?: string[];
        relationEntityId?: string;
        categoryId?: string;
        categoryFieldId: string;
      },
    ) {}
  }
  export class FetchRelationEntriesTags {
    static readonly type = '[Entry Tags] Fetch Entries For Relation Tags';
    constructor(
      public payload: {
        entityId: string;
        viewId?: string;
        relationId: string;
        entryId: string;
        dataQuery?: DataQueryPresenterModel;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        search?: string;
        state?: DataEntryStateEnum;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        appliedTagsFilters?: string[];
      },
    ) {}
  }
  export class FetchRelationEntriesWithoutCount {
    static readonly type = '[Entry] Fetch Entries For Relation without count';
    constructor(
      public payload: {
        entityId: string;
        viewId?: string;
        relationId: string;
        entryId: string;
        dataQuery?: DataQueryPresenterModel;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        search?: string;
        state?: DataEntryStateEnum;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
      },
    ) {}
  }

  export class FetchEntries {
    static readonly type = '[Entry] Fetch Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }

  export class FetchDraftEntries {
    static readonly type = '[Entry] Fetch Draft Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }
  export class CountDraftEntries {
    static readonly type = '[Entry] Count Draft Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }
  export class FetchKanbanDraftEntries {
    static readonly type = '[Entry] Fetch Kanban Draft Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
        categoryFieldId: string;
        categoryId?: string;
      },
    ) {}
  }

  export class FetchEntriesTags {
    static readonly type = '[Entry Tags] Fetch Entries Tags';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
      },
    ) {}
  }

  export class FetchTreeEntries {
    static readonly type = '[Entry] Fetch Tree Entries';
    constructor(
      public payload: {
        entityId: string;
        fieldNormalizedName: string;
        fieldSource?: string;
        startingItem?: string;
        viewId?: string;
        appliedTagsFilters?: string[];
        search?: string;
        appliedQuickFilters?: string[];
        dataQuery?: DataQueryPresenterModel;
      },
    ) {}
  }

  export class FetchTransitoryTreeEntries {
    static readonly type = '[Entry] Fetch Transitory Tree Entries';
    constructor(
      public payload: {
        entityId: string;
        startingItem: string;
        fieldSource?: string;
        fieldNormalizedName?: string;
        viewId?: string;
        appliedTagsFilters?: string[];
        expandAll?: boolean;
        search?: string;
        appliedQuickFilters?: string[];
        dataQuery?: DataQueryPresenterModel;
        relationId?: string;
        relatedEntryId?: string;
      },
    ) {}
  }
  export class SetTreeEntryState {
    static readonly type = '[Entry] Set Tree Entry State';
    constructor(
      public payload: {
        entityId: string;
        startingItem: string;
        state: boolean;
        viewId?: string;
      },
    ) {}
  }
  export class CreateEntry {
    static readonly type = '[Entry] Create Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entry: DataEntryPresenterModel,
      public viewId?: string,
      public selectEntry?: boolean,
      public refValues?: ReferenceValueUpdateModel[],
      public originalDraftId?: string,
      public systemButtonId?: string,
      public saveAsInvalid?: boolean,
    ) {}
  }

  export class QuickCreateEntry {
    static readonly type = '[Entry] Quick Create Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entry: DataEntryPresenterModel,
      public viewId?: string,
      public saveAsInvalid?: boolean,
    ) {}
  }
  export class QuickCreateRelatedEntry {
    static readonly type = '[Entry] Quick Create Related Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entry: DataEntryPresenterModel,
      public relationId: string,
      public viewId?: string,
      public saveAsInvalid?: boolean,
    ) {}
  }

  export class BatchCreateEntryWithRelatedEntries {
    static readonly type = '[Entry] Batch Create Entry With Related Entries';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entry: DataEntryPresenterModel,

      public relatedEntries: {
        entries: DataEntryPresenterModel[];
        relationId: string;
      }[],
      public viewId?: string,
      public selectEntry?: boolean,
      public saveAsInvalid?: boolean,
    ) {}
  }

  export class UpsertDraftEntry {
    static readonly type = '[Entry] Create Draft Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entry: DataEntryPresenterModel,
      public viewId?: string,
      public selectEntry?: boolean,
      public draftId?: string,
    ) {}
  }
  export class UpdateEntry {
    static readonly type = '[Entry] Update Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public payload: DataEntryPresenterModel,
      public viewId?: string,
      public shouldFetchEntry?: boolean,
      public refValues?: ReferenceValueUpdateModel[],
      public systemButtonId?: string,
      public saveAsInvalid?: boolean,
    ) {}
  }

  export class UpdateEntryFieldValue {
    static readonly type = '[Entry] Update Entry Field Value';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
        rootReference?: {
          normalizedName: string;
          dataEntry: string;
        };
      },
      public viewId?: string,
    ) {}
  }
  export class UpdateEntryReferenceFieldMappedValues {
    static readonly type = '[Entry] Update Entry Reference Field Mapped Values';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
        rootReference?: {
          normalizedName: string;
          dataEntry: string;
        };
      }[],
      public viewId?: string,
    ) {}
  }
  export class UpdateRelationEntryFieldValue {
    static readonly type = '[Entry] Update Target Related Entry Field Value';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public relationId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
      },
      public viewId?: string,
    ) {}
  }

  export class UpdateRelationEntryReferenceFieldMappedValues {
    static readonly type =
      '[Entry] Update Target Related Entry Reference Field Mapped Values';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public relationId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
      }[],
      public viewId?: string,
    ) {}
  }

  export class UpdateCollectionEntryFieldValue {
    static readonly type = '[Entry] Update Collection Entry Field Value';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public collectionId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
      },
      public viewId?: string,
    ) {}
  }

  export class UpdateCollectionEntryReferenceFieldMappedValues {
    static readonly type =
      '[Entry] Update Collection Entry Reference Field Mapped Values';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public entryId: string,
      public collectionId: string,
      public payload: {
        fieldName: string;
        fieldValue: unknown;
        fieldIndex?: number;
        fieldUId?: number;
      }[],
      public viewId?: string,
    ) {}
  }
  export class ArchiveEntry {
    static readonly type = '[Entry] Archive Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public payload: DataEntryPresenterModel,
      public viewId?: string,
    ) {}
  }

  export class RemoveDraftEntry {
    static readonly type = '[Entry] Remove Draft Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public payload: DataEntryPresenterModel,
    ) {}
  }
  export class ArchiveEntries {
    static readonly type = '[Entry] Archive Entries';
    constructor(
      /**
       * normalized entity name or entity id
       */

      public entityId: string,
      public entryIds: string[],
      public selectAll?: SelectAllDto | null,
      public viewId?: string,
      public relationId?: string,
    ) {}
  }

  export class FetchEntry {
    static readonly type = '[Entry] Fetch Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entryId: string,
      public viewId?: string,

      public navigationOptions?: {
        includeNavigationLinks: boolean;
        relationId?: string;
        parentId?: string;
        dataQuery?: DataQueryPresenterModel;
        searchTerm?: string;
        entryState?: DataEntryStateEnum;
        entryObsolete?: boolean;
        sort?: Sort[];
        collectionId?: string;
        filtersFist?: {
          [key: string]: any;
        };
      },
      public isDraft?: boolean,
    ) {}
  }

  export class FetchWorkflowStatusEntry {
    static readonly type = '[Entry] fetch Workflow Status';
    constructor(
      public entityId: string,
      public entryId: string,
      public mode?: 'main' | 'execution',
    ) {}
  }

  export class SelectEntry {
    static readonly type = '[Entry] Select Entry';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      /**
       * entry id
       */
      public entryId: string,
    ) {}
  }

  export class ChangeCurrentStepOwner {
    static readonly type = '[Entry] Reassign Step Owner';
    constructor(
      public entity: string,
      public workflowId: string,
      public entry: string,
      public body?: ChangeStepOwnerModel,
      public viewId?: string,
    ) {}
  }

  export class TransitoryFetchActionEntriesCount {
    static readonly type = '[Entry] Total number of activated entries';
    constructor(
      public entityId: string,
      public viewId?: string,
    ) {}
  }

  export class TransitoryFetchEntriesCountForEndUser {
    static readonly type =
      '[Entry] Total number of activated entries for end user';
    constructor(
      public entityId: string,
      public viewId?: string,
    ) {}
  }

  export class ClearAppliedFilter {
    static readonly type = '[Entry] Clear Applied Filter';
  }

  export class UpgradeEntry {
    static readonly type = '[Entry] Upgrade entry';
    constructor(
      public entityId: string,
      public viewId?: string,
      public payload?: DataEntryPresenterModel,
    ) {}
  }

  export class ClearPresentedEntries {
    static readonly type = '[Entry] Clear Entries';
  }

  export class ClearEntityPresentedEntries {
    static readonly type = '[Entry] Clear Entity Presented Entries';
    constructor(
      public entityId: string,
      public excludedIds?: string[],
    ) {}
  }

  export class SetPendingDataQueryForView {
    static readonly type = '[Entries] Set Pending Data Query For View';
    constructor(
      public payload: {
        dataViewId: string;
        dataQuery?: DataQueryPresenterModel;
        dataQueryId?: string;
      },
    ) {}
  }

  export class FetchEntryHistory {
    static readonly type = '[Entry] Fetch Entry History';
    constructor(
      public payload: {
        entryId: string;
        entityId: string;
        lastTimeStamp?: string;
      },
    ) {}
  }

  export class FetchOwnerGroupUsers {
    static readonly type = '[Entry] Fetch owner group users';
    constructor(
      public payload: {
        groupId: string;
      },
    ) {}
  }

  export class UpdateEntryPosition {
    static readonly type = '[Entry] Update Entry Position';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public payload: DataEntryUpdatePositionPresenterModel,
      public viewId?: string,
    ) {}
  }
  export class UpdateEntryRelationPosition {
    static readonly type = '[Entry] Update Entry Relation Position';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public payload: DataEntryUpdatePositionPresenterModel,
      public relationId: string,
      public viewId?: string,
    ) {}
  }

  export class UpdateEntryCollectionPosition {
    static readonly type = '[Entry] Update Entry Collection Position';
    constructor(
      /**
       * normalized entity name or entity id
       */
      public entityId: string,
      public collectionId: string,
      public payload: DataEntryUpdatePositionPresenterModel,
      public viewId?: string,
    ) {}
  }

  export class ExportEntries {
    static readonly type = '[Entry] Export Entries';
    constructor(
      public payload: {
        entityId: string;
        templateId: string;
        level: TemplateLevelEnum;
        type: TemplateTypeEnum;
        viewId?: string;
        search?: string;
        itemsToExport?: string[];
        collectionId?: string;
        dataQuery?: DataQueryPresenterModel;
        state?: DataEntryStateEnum;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        appliedQuickFilters?: string[];
        exportType: TemplateExportTypeEnum;
        sort?: Sort[];
      },
    ) {}
  }

  export class FetchEntryReferenceMappedFieldsValues {
    static readonly type = '[Entry] Fetch Entry Reference Mapped Fields Values';
    constructor(
      public payload: {
        entity: string;
        entryId: string;
        referenceEntryId: string;
        referenceFieldId: string;
      },
    ) {}
  }

  export class FetchKanbanEntries {
    static readonly type = '[Entry] Fetch Kanban Entries';
    constructor(
      public payload: {
        /**
         * normalized entity name or entity id
         */
        entityId?: string;
        viewId?: string;
        skip?: number;
        limit?: number;
        sort?: Sort[];
        state?: DataEntryStateEnum;
        entryIds?: string[];
        entryId?: string;
        search?: string;
        reportId?: string;
        dataQuery?: DataQueryPresenterModel;
        normalizeEntry?: boolean;
        isValid?: boolean;
        isObsolete?: boolean;
        isInvalidRequirement?: boolean;
        filterFirst?: {
          [key: string]: any;
        };
        belongsToCategoryValuesFilter?: KanbanCategoryFilter;
        showPageLoading?: boolean;
        appliedQuickFilters?: string[];
        appliedTagsFilters?: string[];
        categoryId?: string;
        categoryFieldId: string;
        collectionId?: string;
      },
    ) {}
  }
  export class FetchEntryEmails {
    static readonly type = '[Entry] Fetch Entry Emails';
    constructor(
      public entityId: string,
      public entryId: string,
    ) {}
  }

  export class FetchEntryDiscussionsAttachments {
    static readonly type = '[Entry] Fetch Entry Discussions Attachments';
    constructor(
      public entityId: string,
      public entryId: string,
    ) {}
  }

  export class CheckIsTemplateEntryUsed {
    static readonly type = '[Entry] check if template entry used';
    constructor(public entryId: string) {}
  }

  export class BulkUpgradeEntries {
    static readonly type = '[Entry] Bulk Upgrade Entries';
    constructor(
      public entity: string,
      public payload: BulkUpgradeEntriesPresenterModel,
    ) {}
  }

  export class BulkUpdateEntries {
    static readonly type = '[Entry] Bulk Update Entries';
    constructor(
      public entity: string,
      public payload: BulkUpdateEntriesPresenterModel,
    ) {}
  }
  export class ClearContextState {
    static readonly type = '[Entry] Clear Context State';
    constructor(
      public entity: string,
      public viewId?: string,
      public reportId?: string,
      public collectionId?: string,
    ) {}
  }

  export class ClearRelationContextState {
    static readonly type = '[Entry] Clear RelationContext State';
    constructor(
      public relationId: string,
      public viewId?: string,
    ) {}
  }
}
